/*
 * voice-labels/cloud.js — labels in the cloud (Premium shelf), the client side.
 *
 * The sticker's number IS the key: a label put in the cloud is heard by number
 * from any phone (GET /api/voice-labels/{id}, no sign-in — the family), and the
 * owner's other phones pull the whole list (GET /api/voice-labels/mine).
 * Writing is the owner's and Premium's (PUT, the server checks tariff and
 * ceiling); this file only says what the server said.
 *
 * Sync rule, both ways: the copy edited LATER in a browser wins (`updatedAt`,
 * ms) — not the one that reached the server later. The voice travels only when
 * it changed since the last push (`audioAt` > `cloudSyncedAt`), so editing a
 * caption does not re-upload the recording and does not bump its version.
 *
 * No DOM here: tool.js owns the screen and calls these.
 */
(function () {
    'use strict';
    var BASE = '/api/voice-labels';

    function csrf() {
        var m = document.querySelector('meta[name="csrf-token"]');
        return m ? (m.getAttribute('content') || '') : '';
    }
    function bounded(p, ms) {
        if (typeof window.__bounded === 'function') return window.__bounded(p, ms, 'vl_cloud');
        return Promise.race([p, new Promise(function (_, rej) { setTimeout(function () { var e = new Error('WAIT_TIMEOUT'); e.code = 'WAIT_TIMEOUT'; rej(e); }, ms); })]);
    }
    /* A redirect is never an answer of this API: it is the sign-in page. */
    function call(method, url, body, ms) {
        var init = {
            method: method, credentials: 'same-origin', redirect: 'error',
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        };
        if (method !== 'GET') init.headers['X-CSRF-TOKEN'] = csrf();
        if (body) init.body = body;
        return bounded(fetch(url, init), ms || 20000).then(function (res) {
            return res.json().catch(function () { return {}; }).then(function (data) {
                return { status: res.status, ok: res.ok, data: data || {} };
            });
        }, function (err) {
            return { status: 0, ok: false, data: { error: (err && err.code === 'WAIT_TIMEOUT') ? 'timeout' : 'network' } };
        });
    }

    function create(o) {
        var Store = o.store, C = o.core;
        var st = {
            enabled: false, signedIn: false, pro: false,
            cards: {}, cap: 0, used: 0, loaded: false,
            queue: [], busy: false, lastError: '', pushed: 0, pulled: 0,
        };
        function emit() { try { if (o.onChange) o.onChange(st); } catch (e) { } }

        /* The owner's list. Missing labels come into this browser (voice
           downloaded with them), newer cloud copies replace older local ones. */
        function pull() {
            if (!st.signedIn) return Promise.resolve(st);
            return call('GET', BASE + '/mine', null, 20000).then(function (r) {
                if (!r.ok) { st.loaded = true; emit(); return st; }
                st.cards = {};
                (r.data.labels || []).forEach(function (c) { st.cards[c.id] = c; });
                st.cap = r.data.cap | 0; st.used = r.data.used | 0; st.pro = !!r.data.is_pro; st.loaded = true;
                return Store.all().then(function (rows) {
                    var have = {}; rows.forEach(function (x) { have[x.id] = x; });
                    var jobs = [];
                    Object.keys(st.cards).forEach(function (id) {
                        var c = st.cards[id], loc = have[id];
                        if (!loc || (c.updated > (loc.updatedAt || 0)) || (c.version > (loc.cloudVersion || 0) && c.voice)) jobs.push(adopt(c, loc));
                    });
                    return jobs.reduce(function (p, j) { return p.then(j); }, Promise.resolve()).then(function () {
                        st.pulled += jobs.length;
                        emit();
                        return st;
                    });
                });
            });
        }
        /* One cloud card into this browser (returns a job for the chain). */
        function adopt(c, loc) {
            return function () {
                var needVoice = c.voice && (!loc || c.version > (loc.cloudVersion || 0) || !loc.audio);
                var voice = needVoice
                    ? bounded(fetch(c.voice, { credentials: 'same-origin' }), 30000).then(function (res) { return res.ok ? res.arrayBuffer() : null; }).catch(function () { return null; })
                    : Promise.resolve(loc ? loc.audio : null);
                return voice.then(function (ab) {
                    var row = {
                        id: c.id,
                        audio: ab || (loc ? loc.audio : null),
                        mime: ab ? (c.mime || 'audio/mpeg') : (loc ? loc.mime : ''),
                        text: c.updated >= (loc ? loc.updatedAt || 0 : 0) ? c.text : loc.text,
                        lang: c.lang, expires: c.expires, duration: c.duration || (loc ? loc.duration : 0),
                        peaks: loc ? loc.peaks : null,
                        createdAt: loc ? loc.createdAt : (c.updated || Date.now()),
                        updatedAt: Math.max(c.updated || 0, loc ? loc.updatedAt || 0 : 0),
                        audioAt: loc ? (loc.audioAt || 0) : 0,
                        cloudVersion: c.version, cloudSyncedAt: Math.max(c.updated || 0, loc ? loc.updatedAt || 0 : 0),
                    };
                    if (!row.audio && !row.text) return null;
                    return Store.put(row);
                });
            };
        }

        /* Does this local label differ from its cloud copy? */
        function dirty(row) {
            var c = st.cards[row.id];
            if (!c) return true;
            return (row.updatedAt || 0) > (row.cloudSyncedAt || 0) && (row.updatedAt || 0) > (c.updated || 0);
        }
        function push(row) {
            var fd = new FormData();
            fd.append('text', row.text || '');
            fd.append('lang', row.lang || '');
            fd.append('expires', row.expires || '');
            fd.append('duration', String(row.duration || 0));
            fd.append('updated_at', String(row.updatedAt || Date.now()));
            var c = st.cards[row.id];
            var sendVoice = !!row.audio && (!c || !c.voice || (row.audioAt || 0) > (row.cloudSyncedAt || 0));
            if (sendVoice) fd.append('voice', new Blob([row.audio], { type: row.mime || 'audio/mpeg' }), 'voice');
            else if (!row.audio && c && c.voice) fd.append('drop_voice', '1');
            /* multipart and PUT do not mix in PHP: POST with Laravel's _method. */
            fd.append('_method', 'PUT');
            return call('POST', BASE + '/' + encodeURIComponent(row.id), fd, 60000).then(function (r) {
                if (r.ok && r.data.label) {
                    var card = r.data.label;
                    st.cards[row.id] = card;
                    if (r.data.created) st.used++;
                    if (r.data.stale) return adopt(card, row)().then(function () { return { ok: true, stale: true }; });
                    return Store.get(row.id).then(function (cur) {
                        if (!cur) return { ok: true };
                        cur.cloudVersion = card.version;
                        cur.cloudSyncedAt = cur.updatedAt;
                        return Store.put(cur).then(function () { return { ok: true }; });
                    });
                }
                return { ok: false, error: r.data.error || ('http_' + r.status), status: r.status, cap: r.data.cap };
            });
        }
        /* Push every label that differs, one at a time; stops on the first
           refusal that will not change by retrying (tariff, ceiling, sign-in). */
        function sync() {
            if (!st.enabled || !st.pro || st.busy) return Promise.resolve(st);
            st.busy = true; st.lastError = ''; emit();
            return Store.all().then(function (rows) {
                var todo = rows.filter(dirty);
                st.queue = todo.map(function (r) { return r.id; });
                emit();
                var i = 0;
                function next() {
                    if (i >= todo.length) return Promise.resolve();
                    var row = todo[i++];
                    return push(row).then(function (res) {
                        st.queue.shift();
                        if (res.ok) { st.pushed++; emit(); return next(); }
                        st.lastError = res.error;
                        if (res.error === 'taken') { emit(); return next(); }
                        emit();
                        return null;
                    });
                }
                return next();
            }).then(function () { st.busy = false; st.queue = []; emit(); return st; },
                function () { st.busy = false; st.lastError = 'network'; emit(); return st; });
        }
        function remove(id) {
            if (!st.signedIn || !st.cards[id]) return Promise.resolve();
            return call('DELETE', BASE + '/' + encodeURIComponent(id), null, 15000).then(function (r) {
                if (r.ok) { delete st.cards[id]; st.used = Math.max(0, st.used - 1); emit(); }
            });
        }
        /* A sticker's label by number — for any phone, signed in or not. */
        function fetchCard(id, ms) {
            if (!id) return Promise.resolve(null);
            return call('GET', BASE + '/' + encodeURIComponent(id), null, ms || 4000).then(function (r) {
                return r.ok && r.data.label ? r.data.label : null;
            });
        }
        function setEnabled(on) { st.enabled = !!on; emit(); return on ? sync() : Promise.resolve(st); }
        function setAccount(signedIn, pro) {
            st.signedIn = !!signedIn; st.pro = !!pro;
            emit();
        }
        function inCloud(id) { return !!st.cards[id]; }

        return { state: st, pull: pull, sync: sync, push: push, remove: remove, fetchCard: fetchCard, setEnabled: setEnabled, setAccount: setAccount, inCloud: inCloud };
    }

    window.VoiceLabelsCloud = { create: create };
})();
